import React, { memo } from 'react'
import type { Props as SelectProps, OptionProps } from 'react-select';
import Select, { components } from 'react-select'
import styled from 'styled-components'

import { SelectOptionsWrapper as SelectOptionsWrapperBase } from './style'
import theme from 'vars/theme'
import { compareValue } from 'utils/commonFunctions'

// JS styled component (./style.js) lacks typed custom props; widen with a type-only cast
const SelectOptionsWrapper = SelectOptionsWrapperBase as React.FC<
  React.PropsWithChildren<{
    width?: number | string
    color?: string
    isError?: boolean
    style?: React.CSSProperties
    className?: string
    'data-dg-id'?: string
    'data-csv-upload-id'?: string
  }>
>

const StyledSelect = styled(Select)`
  .mono-select__control {
    background-color: white;
    border: 1px solid #cccccc;
  }

  .mono-select__menu {
    background-color: white;
    color: #505155;
    .mono-select__option {
      color: #505155;
    }

    .mono-select__option--is-focused {
      background-color: rgba(77, 79, 83, 0.2);
    }

    .mono-select__option--is-selected {
      color: white;
      background-color: #505155;
    }
  }

  .mono-select__single-value {
    color: #505155;
  }

  .mono-select__indicator > svg {
    fill: ${theme.blueMedium5};
  }
`

type OptionType = {
  label: string
  value: string
  'data-dg-id'?: string
}

const CustomOption = (props: OptionProps<OptionType, false>) => (
  <components.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      ...(props.data['data-dg-id'] ? { 'data-dg-id': props.data['data-dg-id'] } : {}),
    }}
  />
)

const selectComponents = { Option: CustomOption }

type Props = {
  width?: string
  className?: string
  style?: React.CSSProperties
  hasClass?: boolean
  color?: string
  isError?: boolean
  label?: string
  required?: boolean
  value: string | number
  csvUploadId?: string
} & Omit<SelectProps<OptionType, false>, 'value'>

const CustomSelect: React.FC<Props> = (props) => {
  const {
    width,
    className,
    style,
    options,
    placeholder,
    hasClass,
    name,
    color,
    isSearchable,
    isError,
    isDisabled,
    onChange,
    value,
    csvUploadId,
  } = props

  const valueSelect = React.useMemo(() => {
    if (!options) return null

    return options.find((option: OptionType) => compareValue(option.value, value))
  }, [options, value])

  return (
    <div className="d-flex flex-shrink-0" style={{ width }} data-csv-upload-id={csvUploadId}>
      <div className="flex-column align-items-start w-100">
        <SelectOptionsWrapper
          width={width}
          className={className}
          style={style}
          color={color}
          isError={isError}
          data-csv-upload-id={csvUploadId ? `${csvUploadId}-options` : undefined}
        >
          <StyledSelect
            className={hasClass ? 'ml-1' : ''}
            classNamePrefix="mono-select"
            isClearable={false}
            name={name}
            options={options}
            value={valueSelect}
            placeholder={placeholder}
            onChange={onChange}
            isSearchable={isSearchable}
            isDisabled={isDisabled}
            menuPlacement="auto"
            noOptionsMessage={() => '選択値なし'}
            data-csv-upload-id={csvUploadId ? `${csvUploadId}-option-list` : undefined}
            components={selectComponents}
          />
        </SelectOptionsWrapper>
      </div>
    </div>
  )
}

export default memo(CustomSelect)
